import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { createPageUrl } from "@/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Video,
  Image as ImageIcon,
  Calendar,
  ArrowRight,
  ExternalLink,
  Tv,
  Star
} from "lucide-react";

const API_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

export default function Community() {
  const { data: newsPosts = [], isLoading } = useQuery({
    queryKey: ['news-all'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/news`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  return (
    <div className="min-h-screen relative">
      {/* Background - Fixed position */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/ebba8cb85_loading_screen_05.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">
                Community Hub
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Join the community, stay updated, and be part of the journey
            </p>
          </div>

          {/* Community Links */}
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group">
              <MessageSquare className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                Discord
              </h3>
              <p className="text-gray-400 mb-6">
                Join our Discord server to chat with developers and other players in real-time
              </p>
              <Button className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white border-none">
                Join Discord
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </Card>

            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group">
              <Tv className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                Streaming
              </h3>
              <p className="text-gray-400 mb-6">
                Watch live development streams and gameplay on Twitch & YouTube
              </p>
              <div className="flex gap-2">
                <Button className="flex-1 bg-[#9146FF] hover:bg-[#772CE8] text-white border-none">
                  Twitch
                </Button>
                <Button className="flex-1 bg-[#FF0000] hover:bg-[#CC0000] text-white border-none">
                  YouTube
                </Button>
              </div>
            </Card>

            <Link to={createPageUrl("MediaGallery")}>
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group">
                <ImageIcon className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                  Media Gallery
                </h3>
                <p className="text-gray-400 mb-6">
                  Screenshots, concept art, and community-created content
                </p>
                <Button variant="outline" className="w-full border-white text-white hover:bg-white/10 hover:text-white hover:border-white">
                  View Gallery
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Card>
            </Link>
          </div>

          {/* News & Dev Updates */}
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-4xl font-bold text-white">
                Latest Updates
              </h2>
            </div>

            {isLoading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <Card key={i} className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8 animate-pulse">
                    <div className="h-6 bg-gray-800 rounded mb-4 w-3/4" />
                    <div className="h-4 bg-gray-800 rounded mb-2" />
                    <div className="h-4 bg-gray-800 rounded w-5/6" />
                  </Card>
                ))}
              </div>
            ) : newsPosts.length === 0 ? (
              <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">No updates yet</h3>
                <p className="text-gray-500">Check back soon for news and development updates</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {newsPosts.map((post) => (
                  <Card
                    key={post.id}
                    className="bg-[#111111] border-[#8B0000]/20 overflow-hidden hover:border-[#8B0000]/50 transition-all duration-300 cursor-pointer group"
                  >
                    {post.image_url && (
                      <div className="h-52 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {post.is_featured === 1 && (
                          <Badge className="bg-transparent text-white border border-[#8B0000]/60 text-xs">
                            <Star className="w-3 h-3 mr-1 fill-current" /> featured
                          </Badge>
                        )}
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-white group-hover:text-white transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-3">
                        {post.content}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Bottom Info */}
          <div className="mt-20 text-center">
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-12">
              <h3 className="text-2xl font-bold mb-4 text-white">
                Stay Connected
              </h3>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Knight Arena is being built in the open. Join our community channels to follow development,
                provide feedback, and be part of creating the game we've all been waiting for.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-[#5865F2] hover:bg-[#4752C4] text-white border-none">
                  <MessageSquare className="mr-2 w-4 h-4" />
                  Join Discord
                </Button>
                <Button className="bg-[#9146FF] hover:bg-[#772CE8] text-white border-none">
                  <Tv className="mr-2 w-4 h-4" />
                  Watch on Twitch
                </Button>
                <Button className="bg-[#FF0000] hover:bg-[#CC0000] text-white border-none">
                  <Video className="mr-2 w-4 h-4" />
                  Watch on YouTube
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
